import { Chip, styled } from '@mui/material';
import React, { type JSX } from 'react';

import { colorSystem } from '../../theme';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { Text } from '../Text';

interface FilterChipProps<Value> {
    label: string | JSX.Element;
    onChange: (value: Value) => void;
    isSelected: boolean;
    startIcon?: IconElement;
    value: Value;
}

export function FilterChip<Value>({
    label,
    onChange,
    isSelected,
    startIcon,
    value,
}: FilterChipProps<Value>): JSX.Element {
    const onClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        onChange(value);
    };

    const chipStyles = React.useMemo(() => {
        if (isSelected) {
            return {
                background: colorSystem.primary[2],
                border: `1px solid ${colorSystem.primary[3]}`,
            };
        }
        return {};
    }, [isSelected]);

    return (
        <DefaultFilterChipContainer
            label={
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {startIcon && (
                        <StartIcon style={{ fontSize: '16px', lineHeight: '0px', marginLeft: '-2px' }}>
                            {startIcon}
                        </StartIcon>
                    )}
                    <Text variant="body-small">{label}</Text>
                </Flexbox>
            }
            onClick={onClick}
            style={chipStyles}
        />
    );
}

/** Sizes the start icon: call sites pass no size. */
const StartIcon = styled(Text)(iconSlot(16));

const DefaultFilterChipContainer = styled(Chip)({
    background: colorSystem.neutral.white,
    border: `1px solid ${colorSystem.neutral[2]}`,
    '&:hover': {
        background: colorSystem.neutral[1],
        border: `1px solid ${colorSystem.neutral[2]}`,
    },
    '&:focus': {
        background: colorSystem.neutral.white,
    },
});
